import { isSameDay, isWithinInterval } from "date-fns";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import type { EventResponse, Event, Profile, EventVisibilityReason } from "@/api/bale.zod";

import { useGetEvent } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Icon } from "@/components/Icon";
import { TextContent } from "@/components/TextContent";
import { Tomato } from "@/components/Tomato";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { profileDisplayName } from "@/model/profile";

export function EventCard({
  event,
  currentAttendees,
  pendingInvites,
  host,
  onClick,
  response,
  visibilityReason,
  color,
}: {
  event: Event;
  currentAttendees: number;
  pendingInvites: number;
  host: Profile;
  color: string;
  response?: EventResponse | null;
  visibilityReason: EventVisibilityReason;
  onClick?: () => void;
}) {
  if (!event.start_time) throw new TypeError("invalid event for EventCard: must have start_time set");
  const { t } = useTranslation();
  const now = useCurrentTime("@minutely");
  const anchorId = useId();
  const eventDetails = useGetEvent(event.id, {}, { query: { select: (data) => data.data } });

  const isInvited = visibilityReason === "Invited";
  const isHost = visibilityReason === "Hosting";

  const isNotPossiblyVisible =
    eventDetails.data &&
    eventDetails.data.event_participants.length === 1 &&
    !eventDetails.data.event_participants[0].discoverable_distance &&
    pendingInvites === 0;

  const isHappeningNow = isWithinInterval(now, { start: event.start_time, end: event.end_time ?? now });

  return (
    <div
      data-component="EventCard"
      data-event-id={event.id}
      className={twJoin(
        "@container relative w-full max-w-160 scroll-m-safe justify-between rounded-sm border-r-4 bg-white shadow-md",
        !!onClick && "cursor-pointer transition motion-safe:hover:-translate-y-0.5",
        (event.start_time === null || isNotPossiblyVisible) && "opacity-60",
      )}
      style={{ borderRightColor: color }}
      role="presentation"
      onClick={onClick}
    >
      <div className="flex size-full grid-flow-col grid-cols-[minmax(auto,24ch)_1fr] grid-rows-[auto_auto] flex-col gap-x-8 gap-y-4 @lg:grid">
        <div className="flex flex-col gap-1 px-4 pt-4 @lg:pr-0">
          <div className="line-clamp-4 font-subtitle text-lg">
            <Link className="hover:underline" to={`/event/${event.id}`}>
              {event.name || t("untitled-event")}
            </Link>
          </div>
          <div className="truncate font-interest text-placeholder">{profileDisplayName(host)}</div>
        </div>

        <div className="flex flex-col gap-1 px-4 @lg:self-end @lg:pr-0 @lg:pb-4">
          <div className="truncate font-hint text-placeholder">{event.location}</div>
          <div className="font-hint text-placeholder">
            {!event.end_time && t("time", { date: event.start_time })}
            {event.end_time &&
              isSameDay(event.start_time, event.end_time) &&
              t("time-range", { startDate: event.start_time, endDate: event.end_time })}
            {event.end_time &&
              !isSameDay(event.start_time, event.end_time) &&
              t("datetime-range", { startDate: event.start_time, endDate: event.end_time })}
          </div>
          <div className="font-hint text-placeholder">
            {isNotPossiblyVisible && <span className="text-danger">{t("event-is-not-visible")}</span>}
            {!isNotPossiblyVisible &&
              !!pendingInvites &&
              t("event-attendance-with-invites", {
                current: currentAttendees,
                max: event.max_attendees,
                invited: pendingInvites,
              })}
            {!isNotPossiblyVisible &&
              !pendingInvites &&
              t("event-attendance", { current: currentAttendees, max: event.max_attendees })}
          </div>
        </div>

        <TextContent className="line-clamp-6 px-4 @lg:pt-4 @lg:pl-0" content={event.description} />

        <ActionBox className="place-content-end px-2 pb-2 @lg:pl-0">
          <div className="flex flex-row flex-wrap items-center justify-end gap-1 empty:hidden">
            {isHappeningNow && (
              <Tomato attention="active" style={{ anchorName: `--${anchorId}` }}>
                {t("happening-now")}
              </Tomato>
            )}
            {isInvited && response === null && <Tomato>{t("invited")}</Tomato>}
          </div>
          {!isHost && response === "Attending" && <Icon name="user-check" className="m-2" />}
          {!isHost && response === "Ignored" && <Icon name="eye-off" className="m-2" />}
          {!isHost && response === "NotAttending" && <Icon name="user-x" className="m-2" />}
          {isHost && <Icon name="edit" />}
        </ActionBox>
      </div>
    </div>
  );
}
